<template>
  <div
    v-if="currentUser"
    class="mb-4"
  >
    <BRow
      class="pb-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableMemos.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createMemo"
        >
          Create Memo
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableMemos"
      class="mt-2"
      :provider="tableProvider"
      :fields="tableMemos.fields"
      :filter="tableMemos.filter"
      :filter-on="tableMemos.filterOn"
      :sort-by="tableMemos.sortBy"
      :sort-desc="tableMemos.sortDesc"
      :sort-direction="tableMemos.sortDirection"
      :total-rows="tableMemos.totalRows"
      :current-page="tableMemos.currentPage"
      :per-page="tableMemos.perPage"
      :stacked="tableMemos.stacked"
      :busy="tableMemos.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-md-start gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableMemos.busy"
            @click.prevent="editMemo(item)"
          >
            Edit
          </BButton>
          <BButton
            v-if="Number(item.customer_memos_count) <= 0"
            size="sm"
            variant="outline-danger"
            :disabled="tableMemos.busy"
            @click.prevent="cancelMemo(item)"
          >
            Cancel
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalMemo"
      size="lg"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      :title="MMemoTitle"
      @shown="() => attachArrowScroller('.nav-modal-memos-tabs')"
      @hidden="() => detachArrowScroller('.nav-modal-memos-tabs')"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-modal-memos-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>

        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabMemoDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabMemoDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>

          <BRow>
            <BCol
              cols="12"
            >
              <ValidationObserver
                ref="formMemo"
              >
                <form
                  role="form"
                  novalidate
                  @submit.prevent
                >
                  <BRow>
                    <BCol
                      cols="12"
                      md="4"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="memo code"
                        vid="memo_code"
                        rules="required|max:30"
                      >
                        <BFormGroup
                          label="Memo Code"
                          label-for="fMemoCode"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        >
                          <BFormInput
                            id="fMemoCode"
                            v-model="formMemo.memo_code"
                            type="text"
                            maxlength="30"
                            autocomplete="off"
                            placeholder="enter memo code here"
                            :state="errors.length > 0 ? false : null"
                            :disabled="state.busy || Number(formMemo.customer_memos_count) >= 1"
                          />
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </BCol>

                    <BCol
                      cols="12"
                      md="8"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="memo name"
                        vid="memo_name"
                        rules="required|max:100"
                      >
                        <BFormGroup
                          label="Memo Name"
                          label-for="fMemoName"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        >
                          <BFormInput
                            id="fMemoName"
                            v-model="formMemo.memo_name"
                            type="text"
                            maxlength="100"
                            autocomplete="off"
                            placeholder="enter memo name here"
                            :state="errors.length > 0 ? false : null"
                            :disabled="state.busy || Number(formMemo.customer_memos_count) >= 1"
                          />
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </BCol>

                    <BCol
                      cols="12"
                      md="4"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        vid="start_date"
                        name="start date"
                        rules="required"
                      >
                        <BFormGroup
                          label="Start Date"
                          label-for="fStartDate"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        >
                          <BFormInput
                            id="fStartDate"
                            v-model="formMemo.start_date"
                            type="date"
                            :min="formMemoValidation.rangeDate.min"
                            :max="formMemoValidation.rangeDate.max"
                            :state="errors.length > 0 ? false : null"
                            :disabled="state.busy || Number(formMemo.customer_memos_count) >= 1"
                          />
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </BCol>

                    <BCol
                      cols="12"
                      md="4"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        vid="end_date"
                        name="end date"
                        rules="required"
                      >
                        <BFormGroup
                          label="End Date"
                          label-for="fEndDate"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        >
                          <BFormInput
                            id="fEndDate"
                            v-model="formMemo.end_date"
                            type="date"
                            :min="formMemoValidation.rangeDate.min"
                            :max="formMemoValidation.rangeDate.max"
                            :state="errors.length > 0 ? false : null"
                            :disabled="state.busy || Number(formMemo.customer_memos_count) >= 1"
                          />
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </BCol>

                    <BCol
                      v-if="state.editing"
                      cols="12"
                    >
                      <BFormGroup>
                        <BFormCheckbox
                          id="fActive"
                          v-model="formMemo.active"
                          switch
                          value="1"
                          name="active"
                          unchecked-value="0"
                          :disabled="state.busy || Number(formMemo.customer_memos_count) >= 1"
                        >
                          Is Active?
                        </BFormCheckbox>
                      </BFormGroup>
                    </BCol>
                  </BRow>
                </form>
              </ValidationObserver>

            </BCol>
          </BRow>
        </BTab>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-paperclip', {
                  'text-danger fat fa-shake': vTabAttachments()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabAttachments()
                }]"
              >
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :show-uploader="Number(formMemo.customer_memos_count) <= 0"
            :loading="state.busy"
            :valid-id="formMemo.id"
            :path="formMemo.attachment_path"
            :files="formMemo.attachments"
            :deleted-files="formMemo.deleted_files"
            @files:updated="updateMemoAttachments"
          />
        </BTab>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-circle-question', {
                  'text-danger fat fa-shake': vTabRequiredDocuments()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabRequiredDocuments()
                }]"
              >
                Required Documents
              </b>
            </div>
          </template>
          <DocumentTypes
            ref="documentTypes"
            :documents="formMemo.documents"
            @updated:documents="updateMemoDocuments"
          />
        </BTab>
      </BTabs>

      <template #modal-footer="{cancel}">
        <BButton
          v-if="Number(formMemo.customer_memos_count) <= 0"
          size="sm"
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateMemo"
        >
          {{ state.editing ? 'Update' : 'Create' }} Memo
        </BButton>
        <BButton
          size="sm"
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          {{ Number(formMemo.customer_memos_count) >= 1 ? 'Close' : 'Discard' }}
        </BButton>
      </template>
    </BModal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import Attachment from './components/Attachment.vue'
import DocumentTypes from './components/DocumentTypes.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import { SRSMMemo } from '@/services/rsm'

export default {
  name: 'VRSMMemos',

  components: {
    Attachment,
    DocumentTypes
  },

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'rsm'
  ],

  metaInfo: () => ({
    title: 'Customer Memos'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formMemoValidation: {
        rangeDate: {
          min: moment().subtract(2, 'years').startOf('year').format('YYYY-MM-DD'),
          max: moment().endOf('month').format('YYYY-MM-DD')
        }
      },
      formMemo: {
        id: '',
        team_id: '',
        memo_code: '',
        memo_name: '',
        start_date: '',
        end_date: '',
        attachment_path: '',
        attachments: [],
        deleted_files: [],
        documents: [],
        customer_memos_count: 0,
        is_visible: 1,
        active: 1
      },
      tableMemos: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 6, key: 'action', class: 'w-md-125px' },
          { mobile: 2, key: 'memo_code', class: 'min-w-150px' },
          { mobile: 3, key: 'memo_name', class: 'min-w-175px' },
          { mobile: 4, key: 'active', class: 'text-md-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 5, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    MMemoTitle () {
      return this.state.editing ? 'Edit Memo' : 'Create Memo'
    }
  },

  methods: {

    vTabMemoDetails () {
      if (this.$refs.formMemo) {
        const formMemoErrors = this.$refs.formMemo.errors
        return Object.keys(formMemoErrors).some(
          errorKey => formMemoErrors[errorKey]?.length > 0
        )
      }

      return false
    },

    vTabAttachments () {
      if (this.$refs.attachmentUploader) {
        const formDocument = this.$refs.attachmentUploader.$refs.formDocument

        if (formDocument) {
          return Object.keys(formDocument.errors).some(
            errorKey => formDocument.errors[errorKey]?.length > 0
          )
        }
      }

      return false
    },

    vTabRequiredDocuments () {
      if (this.$refs.documentTypes) {
        const formType = this.$refs.documentTypes.$refs.formType

        if (formType) {
          return Object.keys(formType.errors).some(
            errorKey => formType.errors[errorKey]?.length > 0
          )
        }
      }

      return false
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableMemos.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      }

      return await SRSMMemo.get(payload).then(
        ({ data }) => {
          this.tableMemos.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableMemos.totalRows = 0
        return []
      }).finally(() => {
        this.tableMemos.busy = false
      })
    },

    createMemo () {
      this.state.editing = false

      const dateFormat = 'YYYY-MM-DD'

      const minDay = moment().subtract(2, 'years').startOf('year')
      const maxDay = moment().add(50, 'years').endOf('year')

      this.formMemoValidation.rangeDate = Object.assign(this.formMemoValidation.rangeDate, {
        min: minDay.startOf('month').format(dateFormat),
        max: maxDay.endOf('month').format(dateFormat)
      })

      this.formMemo = Object.assign(this.formMemo, {
        id: '',
        team_id: this.currentUser.team,
        memo_code: '',
        memo_name: '',
        customer_memos_count: 0,
        start_date: moment().format(dateFormat),
        end_date: '',
        attachment_path: '',
        attachments: [],
        documents: [],
        deleted_files: [],
        is_visible: 1,
        active: 1
      })

      this.$refs.modalMemo.show()
    },

    editMemo (memo) {
      this.state.editing = true

      this.formMemo = Object.assign(this.formMemo, {
        id: Number(memo.id),
        team_id: memo.team_id,
        customer_memos_count: Number(memo.customer_memos_count) || 0,
        memo_code: memo.memo_code,
        memo_name: memo.memo_name,
        start_date: memo.start_date,
        end_date: memo.end_date,
        attachment_path: memo?.attachment_path || '',
        attachments: memo?.attachments || [],
        documents: memo?.documents || [],
        deleted_files: [],
        is_visible: memo.is_visible || 1,
        active: memo.active
      })

      this.$refs.modalMemo.show()
    },

    updateMemoDocuments (documents) {
      this.formMemo = Object.assign(this.formMemo, {
        documents: documents || []
      })
    },

    updateMemoAttachments (attachment) {
      this.formMemo = Object.assign(this.formMemo, {
        attachment_path: attachment?.attachment_path || '',
        attachments: attachment?.attachments || [],
        deleted_files: attachment?.deleted_files || []
      })
    },

    async validateMemo () {
      await this.$refs.formMemo.validate().then(
        async validMemo => {
          const hasNoAttachments = this.formMemo.attachments.length === 0
          const hasNoDocuments = this.formMemo.documents.length === 0

          if (!validMemo || hasNoAttachments || hasNoDocuments) {
            if (hasNoAttachments) {
              if (this.$refs.attachmentUploader) {
                const formDocument = this.$refs.attachmentUploader?.$refs?.formDocument
                if (formDocument) {
                  formDocument.setErrors({
                    attachments: ['Please attach at least one document.']
                  })
                }
              }
            }

            if (hasNoDocuments) {
              if (this.$refs.documentTypes) {
                const formType = this.$refs.documentTypes?.$refs?.formType
                if (formType) {
                  formType.setErrors({
                    document_type: ['Please add at least one required document.']
                  })
                }
              }
            }
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Memo?</h6>' : '<h6>Create this Memo?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putMemo() : this.postMemo()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formMemo.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postMemo () {
      return await SRSMMemo.post(this.formMemo).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalMemo.hide()
            this.$refs.tableMemos.refetch()
          })
        }
      )
    },

    async putMemo () {
      return await SRSMMemo.put(this.formMemo).then(
        ({ data: { message, memo } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalMemo.hide()

            this.$refs.tableMemos.updateRow({
              id: memo.id,
              team_id: memo.team_id,
              team: memo.team,
              memo_code: memo.memo_code,
              memo_name: memo.memo_name,
              start_date: memo.start_date,
              end_date: memo.end_date,
              attachment_path: memo.attachment_path,
              attachments: memo?.attachments || [],
              documents: memo?.documents || [],
              is_visible: memo?.is_visible || 1,
              active: memo.active,
              updated_at: memo.updated_at
            })
          })
        }
      )
    },

    cancelMemo (memo) {
      return this.swalConfirmWithRemarks({
        html: `<h6>Cancel <strong class="text-danger">${memo.memo_name}</strong>?</h6>`,
        confirmButtonText: 'Mark as Canceled',
        cancelButtonText: 'Keep',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async remarks => {
          this.state.busy = true

          return await SRSMMemo.delete({ id: memo.id, remarks }).then(
            ({ data: { message } }) => {
              this.swalSuccess(message).then(() => {
                this.$refs.tableMemos.refetch()
              })
            }
          ).finally(() => {
            this.state.busy = false
          })
        }
      })
    }
  }
}
</script>
